
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Container, Spinner } from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useParams } from "react-router-dom";
import DynamicBreadcrumb from "../header/breadcrumb";
import customerApi from "../../services/customerapi";

const addressTypeLabels = {
    1: "Home",
    2: "Office",
    3: "Other",
};

const ZoneStatus = ({ row }) => {
    if (row.zoneStatus === "INSIDE_ZONE") {
        return (
            <div>
                <Badge bg="success">Inside Zone</Badge>
                <div className="small mt-1">{row.matchedZone?.branchName || "-"}</div>
                <div className="small text-muted">
                    {row.matchedZone?.zoneName || row.matchedZone?.zoneType || "-"}
                </div>
            </div>
        );
    }

    if (row.zoneStatus === "LOCATION_MISSING") {
        return <Badge bg="secondary">Location Missing</Badge>;
    }

    return <Badge bg="danger">Outside All Zones</Badge>;
};

export default function CustomerAddressList() {
    const { customerId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [customer, setCustomer] = useState(null);
    const [addresses, setAddresses] = useState([]);

    useEffect(() => {
        let mounted = true;

        customerApi.getCustomerAddresses(customerId)
            .then((response) => {
                if (!mounted) return;
                if (response?.status !== "SUCCESS") {
                    throw new Error(response?.message || "Failed to load customer addresses");
                }
                setCustomer(response?.data?.customer || null);
                setAddresses(response?.data?.addresses || []);
            })
            .catch((err) => {
                if (!mounted) return;
                setError(err?.response?.data?.message || err?.message || "Failed to load customer addresses");
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [customerId]);

    const columns = useMemo(() => [
        { field: "sno", headerName: "S NO", width: 75, sortable: false },
        {
            field: "addressName",
            headerName: "Address Name",
            minWidth: 150,
            flex: 0.7,
            renderCell: ({ row }) => (
                <div>
                    <div className="fw-semibold">{row.addressName || "-"}</div>
                    <div className="small text-muted">
                        {addressTypeLabels[row.addressTypeId] || "Address"}
                        {Number(row.defaultStatus) === 1 ? " • Default" : ""}
                    </div>
                </div>
            ),
        },
        {
            field: "fullAddress",
            headerName: "Address",
            minWidth: 300,
            flex: 1.5,
            sortable: false,
        },
        {
            field: "coordinates",
            headerName: "Latitude / Longitude",
            minWidth: 190,
            sortable: false,
            renderCell: ({ row }) => (
                <div className="small">
                    <div>{row.latitude ?? "-"}</div>
                    <div>{row.longitude ?? "-"}</div>
                </div>
            ),
        },
        {
            field: "zoneStatus",
            headerName: "Branch Zone",
            minWidth: 190,
            flex: 0.8,
            sortable: false,
            renderCell: ({ row }) => <ZoneStatus row={row} />,
        },
        {
            field: "action",
            headerName: "Action",
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: ({ row }) => (
                <Button
                    size="sm"
                    variant="primary"
                    disabled={row.latitude === null || row.longitude === null}
                    title={row.latitude === null || row.longitude === null ? "Latitude and longitude are missing" : "View on map"}
                    onClick={() => navigate(`/admin/customers/address/${row.id}/location`)}
                >
                    <FontAwesomeIcon icon="fa-solid fa-map-location-dot" /> View Map
                </Button>
            ),
        },
    ], [navigate]);

    const rows = addresses.map((address, index) => ({
        ...address,
        sno: index + 1,
    }));

    return (
        <Container fluid className="py-3">
            <DynamicBreadcrumb pathArray={[
                { name: "Customers", url: "/admin/customers/all" },
                { name: "Customer Addresses", url: null },
            ]} />

            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h5 className="mb-1">Customer Addresses</h5>
                    <div className="text-muted small">
                        {customer ? `${customer.name || "-"} • ${customer.mobile || "-"}` : ""}
                    </div>
                </div>
                {/* <Button
                    variant="outline-danger"
                    onClick={() => navigate("/admin/customers/out-of-zone-addresses")}
                >
                    Out-of-Zone Addresses
                </Button> */}
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : error ? (
                <Alert variant="danger">{error}</Alert>
            ) : (
                <Box>
                    <DataGrid
                        autoHeight
                        rows={rows}
                        columns={columns}
                        getRowHeight={() => "auto"}
                        disableRowSelectionOnClick
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                        pageSizeOptions={[10, 20, 50]}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                printOptions: { disableToolbarButton: true },
                                csvOptions: { disableToolbarButton: true },
                            },
                        }}
                        sx={{ "& .MuiDataGrid-cell": { py: 1 } }}
                    />
                </Box>
            )}
        </Container>
    );
}